import { css } from '@styled-system/css';
import { useEffect, useState } from 'react';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Message } from '@/shared/components/ui/Message';
import { Separator } from '@/shared/components/ui/Separator';
import { useCreateAnalysis } from '@/shared/hooks/analysis/mutations';
import type { AnalysisType } from '@/shared/types/analysis';

interface CreateAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: AnalysisType;
  onSuccess?: (analysisId?: string) => void;
}

export const CreateAnalysisModal = ({ isOpen, onClose, type, onSuccess }: CreateAnalysisModalProps) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { createAnalysis, isCreating, error: createAnalysisError } = useCreateAnalysis();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setUrl('');
      setError(null);
    }
  }, [isOpen]);

  // Set error from mutation
  useEffect(() => {
    if (isOpen) {
      if (createAnalysisError) {
        setError(createAnalysisError);
      } else {
        setError(null);
      }
    }
  }, [isOpen, createAnalysisError]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const normalizeVintedUrl = (url: string): string => {
    try {
      const urlObj = new URL(url.trim());

      // Forcer le protocole HTTPS et le sous-domaine www
      urlObj.protocol = 'https:';
      if (!urlObj.hostname.startsWith('www.')) {
        urlObj.hostname = 'www.' + urlObj.hostname;
      }

      const pathParts = urlObj.pathname.split('/').filter(Boolean); // ex: ['catalog', '2632-baskets', 'brand', '2239-ugg']

      if (pathParts[0] === 'catalog') {
        // Cas 1 : /catalog/1234-category
        const categoryMatch = pathParts[1]?.match(/^(\d+)-/);
        const brandMatch = pathParts.includes('brand')
          ? pathParts[pathParts.indexOf('brand') + 1]?.match(/^(\d+)-/)
          : null;

        if (categoryMatch || brandMatch) {
          const searchParams = new URLSearchParams(urlObj.search);

          // Ajouter le paramètre catalog[]
          if (categoryMatch) {
            searchParams.append('catalog[]', categoryMatch[1]);
          }

          // Ajouter le paramètre brand_id si présent
          if (brandMatch) {
            searchParams.append('brand_id[]', brandMatch[1]);
          }

          // Reconstruire une URL de recherche valide
          urlObj.pathname = '/catalog';
          urlObj.search = searchParams.toString();
        }
      }

      return urlObj.toString();
    } catch (error) {
      console.error('Invalid URL format:', error);
      return url;
    }
  };

  // Fonction de validation d'URL Vinted
  const validateVintedUrl = (url: string): string | null => {
    if (!url) {
      return "L'URL est requise";
    }

    try {
      const urlObj = new URL(url);

      // Vérifier que c'est un domaine Vinted valide
      const validDomains = [
        'vinted.fr',
        'www.vinted.fr',
        'vinted.com',
        'www.vinted.com',
        'vinted.be',
        'www.vinted.be',
        'vinted.de',
        'www.vinted.de',
        'vinted.it',
        'www.vinted.it',
        'vinted.es',
        'www.vinted.es',
        'vinted.nl',
        'www.vinted.nl',
        'vinted.pt',
        'www.vinted.pt',
        'vinted.pl',
        'www.vinted.pl',
        'vinted.cz',
        'www.vinted.cz',
        'vinted.lu',
        'www.vinted.lu',
        'vinted.lt',
        'www.vinted.lt',
      ];

      if (!validDomains.includes(urlObj.hostname)) {
        return "L'URL doit être une URL Vinted valide (ex: vinted.fr, vinted.com, etc.)";
      }

      // Vérifier les chemins selon le type d'analyse
      if (type === 'product') {
        // Pour les produits : doit commencer par /catalog
        if (urlObj.pathname !== '/catalog') {
          return "L'URL doit être une URL de recherche de produits Vinted (commençant par /catalog?...)";
        }
      } else if (type === 'vendor') {
        // Pour les vendeurs : /member/
        if (!urlObj.pathname.startsWith('/member/')) {
          return "L'URL doit être une URL de profil vendeur Vinted (/member/...)";
        }
      }

      return null;
    } catch (error) {
      console.error('Invalid URL format:', error);
      return "L'URL n'est pas valide";
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !url.trim()) {
      return;
    }

    // Normaliser l'URL Vinted
    const normalizedUrl = normalizeVintedUrl(url);
    setUrl(normalizedUrl);

    // Valider l'URL Vinted
    const urlValidationError = validateVintedUrl(normalizedUrl);
    if (urlValidationError) {
      setError(urlValidationError);
      return;
    }

    // Reset error if URL is valid
    setError(null);

    const result = await createAnalysis({
      title: name.trim(),
      url: normalizedUrl,
      type,
    });

    if (result.success && result.analysis) {
      onSuccess?.(result.analysis.id);
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const overlayStyles = css({
    position: 'fixed',
    inset: 0,
    bg: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    p: '1rem',
  });

  const modalStyles = css({
    bg: 'base.bg', // #0f1012
    rounded: 'sm', // 6px from design (rounded-[10px] in Figma = ~6px in our system)
    border: '0.75px solid',
    borderColor: 'base.border', // #1e1f24
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    maxW: '43.75rem', // Approximativement 700px
    w: 'full',
    p: 0,
    overflow: 'hidden',
  });

  const headerStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pt: '1.375rem', // 22px du Figma
    pb: 'sm', // 11px pour correspondre au Figma
    px: '1.125rem', // 18px
    position: 'relative',
  });

  const titleStyles = css({
    fontSize: '0.8125rem', // 13px
    fontWeight: 'medium',
    fontFamily: 'Manrope, sans-serif',
    color: 'font.primary', // #e6e6e9
    w: 'full',
    textAlign: 'left',
    lineHeight: '1.07',
  });

  const contentStyles = css({
    px: '1.125rem', // 18px
    pb: '1.125rem',
    pt: '1.8125rem', // 29px après le separator pour correspondre exactement au Figma
  });

  const formStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '1.625rem', // 26px (approximatif entre les inputs)
  });

  const buttonGroupStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.625rem', // 10px
  });

  const helpLinkStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: 'xxs', // 2px
    mt: '0.75rem',
  });

  const linkTextStyles = css({
    fontSize: '2xs', // 9px
    fontWeight: 'medium',
    fontFamily: 'Manrope, sans-serif',
    color: 'font.secondary', // #b3b5bd
    textDecoration: 'underline',
    cursor: 'pointer',

    _hover: {
      color: 'font.primary',
    },
  });

  const getTitle = () => {
    switch (type) {
      case 'product':
        return 'Ajouter un produit à analyser';
      case 'vendor':
        return 'Ajouter un vendeur à analyser';
      default:
        return 'Ajouter une analyse';
    }
  };

  const getUrlPlaceholder = () => {
    switch (type) {
      case 'product':
        return 'URL Vinted de la recherche produit..';
      case 'vendor':
        return 'URL Vinted du profil vendeur..';
      default:
        return 'URL Vinted..';
    }
  };

  const handleFindUrlClick = () => {
    let helpUrl = '';
    switch (type) {
      case 'product':
        helpUrl = 'https://youtu.be/VkfPn1EhOmI';
        break;
      case 'vendor':
        helpUrl = 'https://youtu.be/UmJFreRaRIA';
        break;
    }

    window.open(helpUrl, '_blank');
  };

  return (
    <div className={overlayStyles} onClick={handleBackdropClick}>
      <div className={modalStyles} onClick={(e) => e.stopPropagation()}>
        <div className={headerStyles}>
          <h2 className={titleStyles}>{getTitle()}</h2>
        </div>

        <Separator />

        <div className={contentStyles}>
          <form className={formStyles} onSubmit={handleSubmit}>
            <Input
              placeholder="Entrer le nom de l'analyse.."
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreating}
              required
            />

            <div>
              <Input
                placeholder={getUrlPlaceholder()}
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError(null); // Reset error when URL changes
                }}
                disabled={isCreating}
                type="url"
                required
              />
              {error && (
                <Message className={css({ mt: '0.5rem' })} type="error">
                  {error}
                </Message>
              )}

              <div className={helpLinkStyles}>
                <Message type="info">
                  <span className={linkTextStyles} onClick={handleFindUrlClick}>
                    Comment trouver l'URL ?
                  </span>
                </Message>
              </div>
            </div>

            <div className={buttonGroupStyles}>
              <Button handleClick={onClose} disabled={isCreating}>
                Annuler
              </Button>
              <Button
                handleClick={handleSubmit}
                showLoader
                disabled={isCreating || !name.trim() || !url.trim()}
                icon="blitz"
                color="white"
                className={css({
                  bg: 'accent.primary !important',
                  _hover: {
                    bg: 'accent.primaryHover !important',
                  },
                })}
              >
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    height: '0.5625rem',
                  })}
                >
                  Ajouter
                </div>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
